import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Clock } from "@phosphor-icons/react";

const VIDEOS = [
  { id: "ScMzIvxBSi4", title: "Inside Healing Labs Lagos", category: "Inventions", length: "12 min", description: "A field report from West Africa's most ambitious healing-technology collective." },
  { id: "aqz-KE-bpKQ", title: "Andes Wisdom — Voices of the Elders", category: "Wisdom", length: "18 min", description: "Recorded over three weeks in the high Andes with knowledge keepers and translators." },
  { id: "9bZkp7q19f0", title: "Quantum Sensing for Ecological Restoration", category: "Earth", length: "9 min", description: "A Seoul-based lab introduces field-grade quantum sensing for soil ecosystems." },
  { id: "ktvTqknDobU", title: "Six Principles of the AI Council", category: "AI", length: "15 min", description: "A roundtable on transparency, plurality, healing, wisdom, sovereignty and joy." },
  { id: "kJQP7kiw5Fk", title: "Decentralized Water — Pacific Stories", category: "Earth", length: "11 min", description: "Five island communities turning desalination into a shared civic infrastructure." },
  { id: "RgKAFK5djSk", title: "Inventors at 60 — A Different Pace", category: "Stories", length: "14 min", description: "Why a generation of senior inventors is changing how we build." },
];

const CATEGORIES = ["All", "Inventions", "Wisdom", "Earth", "AI", "Stories"];

export default function WinTV() {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? VIDEOS : VIDEOS.filter((v) => v.category === active);
  const featured = filtered[0] ?? VIDEOS[0];

  return (
    <div data-testid="page-wintv">
      {/* HERO */}
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 lg:py-28">
          <p className="overline text-gold">WinTV</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight max-w-4xl">
            A media hub for stories, inventions, and global innovation news.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed">
            Interviews with inventors and elders. Field documentaries from labs
            and communities. Wisdom that mainstream platforms rarely show.
          </p>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <p className="overline mb-4">Featured</p>
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-ink ring-1 ring-ink/10 card-shadow" data-testid="featured-video">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${featured.id}`}
                title={featured.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          <div className="lg:col-span-4">
            <p className="overline">{featured.category}</p>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl tracking-tight text-ink leading-tight">{featured.title}</h2>
            <p className="mt-4 text-ink/65 leading-relaxed">{featured.description}</p>
            <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/50">
              <Clock size={14} /> {featured.length}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES + GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="wintv-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`category-${c.toLowerCase()}`}
              className={`text-sm px-4 py-2 rounded-full ring-1 transition ${
                active === c
                  ? "bg-ink text-white ring-ink"
                  : "bg-white text-ink/70 ring-ink/15 hover:text-ink hover:ring-ink/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="video-grid">
          {filtered.map((v, i) => (
            <motion.a
              key={v.id}
              href={`https://www.youtube.com/watch?v=${v.id}`}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.04 }}
              data-testid={`video-card-${v.id}`}
              className="group block bg-white rounded-2xl overflow-hidden ring-1 ring-ink/10 card-shadow hover:-translate-y-1 transition-transform"
            >
              <div className="relative aspect-video bg-ink">
                <img src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-ink/30 group-hover:bg-ink/40 transition" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex w-14 h-14 rounded-full bg-gold text-ink items-center justify-center">
                    <Play size={22} weight="fill" />
                  </span>
                </div>
                <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase bg-white/90 text-ink px-2 py-1 rounded">
                  {v.category}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl text-ink leading-snug">{v.title}</h3>
                <p className="mt-2 text-sm text-ink/60">{v.description}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/50">
                  <Clock size={12} /> {v.length}
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>
    </div>
  );
}
